import { ISubmissionSyntheticVariables } from '@publishing-engine/submission-service/src/workflow-handling/types';
import {
  IChangeRequestWithSubmissionData,
  IDocumentType,
  IMergedAccount,
  IReviewer,
  IWorkflowData
} from './index';

type IFileUploadData = {
  progress: number;
  isUploading: boolean;
  fileId: string;
};

export type IDependentValue<T> =
  | {
      exists: true;
      value: T;
    }
  | {
      exists: false;
      value?: T;
    };

export type IDependentValueWithText<T> =
  | {
      exists: true;
      value: T;
      description: string;
    }
  | {
      exists: false;
      value?: T;
      description?: string;
    };

export enum FundingGenricExistsEnum {
  NOT_KNOWN = 'not known',
  SELF_FUNDED = 'self funded',
  EXTERNALLY_FUNDED = 'externally funded'
}
export type IDependentFundingGenericValue<T> = {
  exists: FundingGenricExistsEnum;
  value: T;
};
export type IParticipant = {
  role: string;
  userId: string;
};

export type IRevisionFeedback = {
  type: 'revision';
  id: string;
  payload: IRevisionRequest & {
    authorResponse?: string;
  };
};

export type IDecisionFeedbackOption = 'reject' | 'accept' | 'withdraw';

export type IDecisionFeedback = {
  type: 'decision';
  id: string;
  payload: {
    decision: IDecisionFeedbackOption;
    roleType: 'editorial' | 'author';
    comment?: string;
    reason?: string;
  };
};

export type IFeedback = IRevisionFeedback | IDecisionFeedback;

export type IAuthor = {
  id?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  affiliation: string;
  ringgoldId?: string;
  orcid?: string;
  email?: string;
  ccEmail?: string;
  secondaryEmail?: string;
  country?: string;
  countryCode?: string;
  authorType?: string;
  city?: string;
  postalCode?: string;
  lastSentEmailStatus?: string;
  lastSentEmailTimeStamp?: number;
  lastSentStatus?: string;
  lastSentTimeStamp?: string;
  isEmailDelivered?: boolean;
  secondaryEmailDetails?: { emailTimeStamp: string; isEmailDelivered: boolean };
  ccEmailDetails?: { emailTimeStamp: string; isEmailDelivered: boolean };
  mergedAccount?: IMergedAccount;
  degree?: string;
};

export type IAuthorRequirementOptions = Extract<
  keyof IAuthor,
  'orcid' | 'affiliation' | 'ringgoldId'
>;

export type IAuthorWithEmail = IAuthor & {
  email: string;
};

export type IAuthorWithEmailAndOrcid = IAuthorWithEmail & {
  orcid: string;
};

export type ISubmissionFile = {
  id?: string;
  referenceName: string;
  name: string;
  extension: string;
  url: string;
  visibility?: string;
  errors?: string[];
  warnings?: string[];
} & Partial<IFileUploadData>;

export type ISubmissionFiguresAndTables = {
  referenceName: string;
  isReproduced: boolean;
  sourceFile?: ISubmissionFile;
  permissionEvidence?: ISubmissionFile;
}[];

export type IImageMetadata = {
  size: number;
  height: number;
  width: number;
  pixels: number;
};

export type IFiguresAndTablesFile = {
  caption: string;
  altText?: string;
  file?: ISubmissionFile;
  metadata?: IImageMetadata;
  isComposite?: boolean;
  subParts?: {
    id: string;
    caption: string;
    altText?: string;
    file: ISubmissionFile;
    metadata?: IImageMetadata;
  }[];
}[];

export type IConvertedFiles = {
  key: string;
  value: {
    eventId: string;
    oldFileUrl: string;
    file: ISubmissionFile;
  }[];
}[];

export type IConsolidatedPdfJobStatus =
  | 'creating'
  | 'processing'
  | 'completed'
  | 'failed';

export interface IConsolidatedPdfJob {
  status: IConsolidatedPdfJobStatus;
  startedAt: string;
  editorJobId?: string;
  anonymizedJobId?: string;
  error?: string;
  /**
   * Number of leading pages (cover page + revision-details pages) to exclude
   * from page numbering for each variant. Set when the job is created so the
   * finalized PDF can number only the manuscript pages.
   */
  editorLeadingPageCount?: number;
  anonymizedLeadingPageCount?: number;
}

export type ISubmissionFileSet = {
  Manuscript: ISubmissionFile;
  AnonymizedManuscript?: ISubmissionFile;
  TitlePage?: ISubmissionFile;
  Supplementary?: ISubmissionFile[];
  AdditionalSupplementary?: ISubmissionFile[];
  CoverLetter?: ISubmissionFile;
  FiguresAndTables?: IDependentValue<ISubmissionFiguresAndTables>;
  SourceFiles?: ISubmissionFile[];
  // AnonymizedManuscriptWithTrackedChanges?: ISubmissionFile;
  CopyrightForm?: ISubmissionFile;
  LicensesAndPermissionsFiles?: ISubmissionFile[];
  AdditionalFiles?: ISubmissionFile[];
  ExportPreprodSubmission?: ISubmissionFile;
  AuthorsCopyrightForm?: IAuthorCopyrightForm;
  AuthorsICMJEForm?: IAuthorICMJEForm;
  CONSORTFiles?: ISubmissionFile[];
  ARRIVEFiles?: ISubmissionFile[];
  Figures?: IFiguresAndTablesFile;
  Tables?: IFiguresAndTablesFile;
  GraphicalAbstractImage?: IFiguresAndTablesFile;
  ConvertedFiles?: IConvertedFiles;
  StandardReportingGuidelinesFileDetails?: ISubmissionFile[];
  ConsolidatedPdf?: ISubmissionFile;
  AnonymizedConsolidatedPdf?: ISubmissionFile;
  ConsolidatedPdfJob?: IConsolidatedPdfJob;
};

export type IPublishingModel =
  | {
      model: 'subscription';
    }
  | {
      model: 'openAccess';
      acknowledgeCharge: true;
    };

export type IDataAvailability = {
  status:
    | 'openly-available'
    | 'embargo-period'
    | 'included-in-article'
    | 'not-available';
};

export type SubmissionAuthorContribution = {
  contribution: string;
  degree: string;
};

export type IAuthorWithContribution = IAuthorWithEmail & {
  contributions?: Array<SubmissionAuthorContribution>;
};

export type IFunding = {
  fundingBody: string;
  grantNumber: string;
};
export type IFundingGeneric = {
  fundingBody: string;
  grantNumber: string;
};
export type IRevisionRequest = {
  revisionSeverity?: IRevisionSeverity;
  editorComment: string;
  editorAttachments?: ISubmissionFile[];
  reviews?: {
    comment: string;
  }[];
};

export type ISubmissionMetadata = {
  title: string;
  englishTitle: string;
  subtitle?: string;
  englishSubtitle?: string;
  abstract: string;
  englishAbstract: string;
  articleSubtype?: string;
  keywords: string[];
  englishKeywords?: string[];
  authors: IAuthorWithContribution[];
  ethicsStatement?: IDependentValue<string>;
  clinicalTrials?: IDependentValue<string>;
  previouslySubmitted?: IDependentValue<string>;
  conflictOfInterestForAuthor?: IDependentValue<string>;
  aiTools?: IDependentValueWithText<string[]>;
  preprint?: IDependentValue<string>;
  noveltyStatement?: string;
  funding?: IDependentValue<IFunding[]>;
  fundingGeneric?: IDependentFundingGenericValue<IFundingGeneric[]>;
  LicensesAndPermissions?: string;
  LicensesAndPermissionsReproducedContent?: string;
  coauthorsAgreeToEthics?: boolean;
  coauthorsAgreeToSharing?: boolean;
  coauthorManuscriptOriginalityConfirmed?: boolean;
  coauthorManuscriptNotUnderConsiderationElsewhere?: boolean;
  coauthorAuthorshipAndConflictDeclaration?: boolean;
  coauthorResearchEthicsComplianceConfirmed?: boolean;
  coauthorExportControlComplianceConfirmed?: boolean;
  publicationConsentConfirmed?: boolean;
  privacyPolicyConsentConfirmed?: boolean;
  submissionAgreementConfirmed?: boolean;
  manuscriptOwnership?: boolean;
  apcAgreement?: boolean;
  blindedReviewConsent?: boolean;
  openAccessPublishingConsent?: boolean;
  authorDeclaration?: boolean;
  ethicalGuidelinesMet?: boolean;
  articleLanguage?: string;
  ethicsChecklist?: boolean;
  sharedDataset?: IDependentValue<string>;
  generatedDataset?: boolean;
  utilizedDataset?: boolean;
  overlappingMaterial?: boolean;
  ethicalApproval?: IDependentValue<string>;
  ethicsApproval?: IDependentValue<string>;
};

export type IReviewerRecommendation = IAuthorWithEmail & { reason: string };

export type ISubmissionPreference = {
  immediatePublication?: boolean;
  standardReportingGuidelines?: boolean;
  standardReportingGuidelinesDetails?: string[];
  dataAvailabilityStatement?: string[];
  dataAvailabilityText?: string;
  transparentPeerReview?: boolean;
  doubleAnonymousPeerReview?: boolean;
  publishingModel?: IPublishingModel;
  dataAvailability?: IDataAvailability;
  dataAvailabilityOpenlyAvailableUrl?: string;
  dataAvailabilityEmbargoUrl?: string;
  dataAvailabilityEmbargoDate?: string;
  dataUnavailabilityReason?: string;
  recommendedReviewers?: IDependentValue<IReviewerRecommendation[]>;
  opposedReviewers?: IDependentValue<IReviewerRecommendation[]>;
  acceleratedPublication?: string;
  ipemMembership?: string;
  subjectArea?: string;
  openAccessChoice?: string;
  openAccessChoiceWorldBankACountry?: string;
  openAccessChoiceWorldBankBCountry?: string;
  openAccessChoiceReviewerDiscountRate?: string;
  focusIssue?: string;
  category?: string;
  numberOfFigures?: number;
  numberOfTables?: number;
  numberOfColorFigures?: number;
  numberOfWords?: number;
  otherCategory?: string;
  blindedPeerReviewConsent?: boolean;
  copyrightLicensingForm?: boolean;
  submissionGuidelines?: boolean;
  volume?: number;
  issue?: string;
  coAuthorApproval?: string;
  generalClassifications?: string[];
  commentForEditor?: string;
  standardReportingGuidelinesText?: string;
};

// For launch, we only care about 'CC-BY-4.0', but in the long run there will be more.
export type ILicenseType = 'CC-BY-4.0';

export type ICopyrightType = 'Gold Open Access' | 'Subscription' | 'Other';

export type ISubmissionLicensing = {
  copyrightType?: ICopyrightType;
  copyrightHolder?: string;
  transformativeAgreementStatus?: string;
  transformativeAgreementDateRequested?: string;
  transformativeAgreementDateChased?: string;
  transformativeAgreementWaiverPercentage?: number;
  transformativeAgreementInstitution?: string;
  openAccessDateRequested?: string;
  openAccessLicenseType?: ILicenseType;
  openAccessWaiverContact?: string;
  openAccessWaiverComment?: string;
  openAccessWaiverReason?: string;
  openAccessWaiverPercentage?: number;
  openAccessWaiverAmount?: number;
};

type IIntegrityResultStatus = 'processing' | 'success' | 'failed';
export type IIntegrityResultIconStatus =
  | 'PROCESSING'
  | 'SUCCESS'
  | 'FAIL'
  | 'WARNING';

export type IUiIntegrityResultIconStatus =
  | 'PROCESSING'
  | 'SUCCESS'
  | 'FAIL'
  | 'WARNING'
  | 'COMPLETE';
export type IIntegrityCheckResult = {
  type: string;
  name: string;
  status: IIntegrityResultStatus;
  description: string;
  iconStatus: IIntegrityResultIconStatus;
  error?: {
    message: string;
  };
};

export type IUiIntegrityCheckResult = {
  checkType: string;
  status: IUiIntegrityResultIconStatus;
  result: {
    showDetailedReport: boolean;
    status: IIntegrityResultIconStatus;
    message: string;
    occurrences?: {
      key: string;
      value: string;
      status: 'PASS' | 'FAIL' | string;
    }[];
  };
  metadata: {
    pageCount?: number;
    wordCount?: number;
    characterCount?: number;
    overallMatchPercentage?: number;
    scanId?: string;
    localDuplicateCount?: number;
    externDuplicateCount?: number;
    alterationCount?: number;
    aiImageCount?: number;
    resultUrl?: string;
    pdfReport?: string;
    minScore?: number;
  };
  externalId: string;
  created_at: string;
  update_at: string;
};

type IntegrityReportUrl = { reportURL: string };
type IntegrityBatchId = { batchID: string };

export type IIntegrity = IntegrityReportUrl &
  IntegrityBatchId & {
    results: IIntegrityCheckResult[];
  };

export type IUiIntegrity = IntegrityReportUrl &
  IntegrityBatchId & {
    checkResults: IUiIntegrityCheckResult[];
  };

export type IIntegrityResponseFromRI = IntegrityReportUrl &
  IntegrityBatchId & {
    check: IIntegrityCheckResult;
  };

export type ICopyrightForm = {
  ownership: IDependentValue<string>;
  type: string;
  typeReason?: string;
  waiver?: boolean;
  articleProcessingCharge?: boolean;
  submittingAuthorSignature?: string;
  authorsFullNames?: string[];
  agreement: boolean;
  file?: ISubmissionFile;
  responseToAuthor?: string;
  attachments?: ISubmissionFile[];
  date?: string;
  submittedBy: string;
};

export type IAuthorCopyrightForm = {
  userId?: string;
  email: string;
  file: ISubmissionFile;
  isSubmittingAuthor: boolean;
  date: string;
  editorUpdated?: boolean;
};

export type IAuthorICMJEForm = {
  userId?: string;
  email: string;
  file: ISubmissionFile;
  isSubmittingAuthor: boolean;
  date: string;
  isRequested?: boolean;
  editorUpdated?: boolean;
  isSubmitted?: boolean;
};

export type IAuthorICMJEFormList = {
  name: string;
  isUploaded: boolean;
} & IAuthorICMJEForm;

export type ISubmission = {
  schemaId: string;
  type: IDocumentType;
  metadata: ISubmissionMetadata;
  submittingAuthor: string;
  correspondingAuthor?: string;
  contactAuthor?: string;
  fileSet: ISubmissionFileSet;
  exportUrl?: string;
  preference: ISubmissionPreference;
  integrity?: IIntegrity;
  licensing?: ISubmissionLicensing;
  authorResponse?: string;
  authorResponseAttachments?: ISubmissionFile[];
  isFileUpdated?: boolean;
};

export type IEditSubmission = {
  metadata?: Partial<ISubmissionMetadata>;
  fileSet?: Partial<ISubmissionFileSet>;
  preference?: Partial<
    Omit<
      ISubmissionPreference,
      | 'dataAvailability'
      | 'dataUnavailabilityReason'
      | 'immediatePublication'
      | 'acknowledgePublicationCharge'
    >
  >;
  licensing?: Partial<ISubmissionLicensing>;
  submittingAuthor?: Partial<IAuthorWithEmailAndOrcid>;
  correspondingAuthor?: Partial<IAuthorWithEmail>;
  contactAuthor?: Partial<IAuthorWithEmail>;
};

export type IChangeArticleType = {
  newName: string;
  newTypeId: string;
};

export const REVISION_SEVERITY_OPTIONS = [
  'minor',
  'moderate',
  'major'
] as const;
export type IRevisionSeverity = string;
export const REVISION_SEVERITY_OPTIONS_TO_TEXT = {
  minor: 'Minor Revision',
  moderate: 'Moderate Revision',
  major: 'Major Revision'
} as const;

export const getRevisionSeverityText = (severity?: string): string =>
  severity
    ? ((REVISION_SEVERITY_OPTIONS_TO_TEXT as Record<string, string>)[
        severity
      ] ?? severity.charAt(0).toUpperCase() + severity.slice(1))
    : '';

export type IReviewerFeedback = {
  referenceAssessmentId: string;
  reviewId: string;
  comment: string;
  attachments?: ISubmissionFile[];
  recommendation?: string;
  reviewerInformation?: IReviewer;
};

export type IAdjudicatorFeedback = {
  referenceAssessmentId?: string;
  adjudicationId: string;
  comment: string;
  attachments?: ISubmissionFile[];
  recommendation?: string;
};

export type IAdjudicatorStatus =
  | 'invited'
  | 'assessment'
  | 'reviewed'
  | 'expired'
  | 'declined'
  | 'interrupted';

export type IInvitedAdjudicator = {
  adjudicationId: string;
  adjudicatorId: string;
  firstName?: string;
  lastName?: string;
  email: string;
  affiliation?: string;
  status: IAdjudicatorStatus;
  dateInvited: string;
  dateResponded?: string;
  declineReason?: string;
};

export type IReviewerStatus =
  | 'invited'
  | 'declined'
  | 'expired'
  | 'rescinded'
  | 'firstAssessment'
  | 'submittedFirstAssessment'
  | 'revisionInvited'
  | 'revisionAssessment'
  | 'submittedRevisionAssessment'
  | 'reviewed';

export type IInvitedReviewer = {
  reviewId: string;
  reviewerId: string;
  firstName?: string;
  lastName?: string;
  email: string;
  affiliation?: string;
  status: IReviewerStatus;
  dateInvited: string;
  dateResponded?: string;
  declineReason?: string;
  isBoardMember?: boolean;
  isInActiveReview?: boolean;
  /** Set when revision history is loaded for editors; one rating per peer review. */
  isReviewRated?: boolean;
  /** Server-computed; use this alone to show rate-review actions in the UI. */
  canRateReview?: boolean;
  workflowCurrentState?: string;
  workflowPreviousState?: string;
};

export type IRevisionRoundType = 'immediate' | 'review' | 'transfer';

export type IRevisionRound = {
  id: string;
  dateEntered: string;
  type: IRevisionRoundType;
  dateCompleted?: string;
  severity?: IRevisionSeverity;
  editorialComment: string;
  editorialAttachments?: ISubmissionFile[];
  invitedReviewers?: IInvitedReviewer[];
  reviewerOriginalFeedback?: IReviewerFeedback[];
  invitedAdjudicators?: IInvitedAdjudicator[];
  adjudicatorOriginalFeedback?: IAdjudicatorFeedback[];
  authorResponse?: string;
  authorResponseAttachments?: ISubmissionFile[];
  updatedManuscript?: ISubmissionFile;
  previousManuscript?: ISubmissionFile;
  reviewerRevisionFeedback?: IReviewerFeedback[];
  editorialDecision?: string;
  editorialDecisionEventId?: string;
};

export type ITargetJournals = {
  id: string;
  articleTypeCategoryId: string;
};

export type ITransferOffer = {
  dateEntered: string;
  transferReason?: string;
  editorComment?: string;
  targetJournals?: ITargetJournals[];
  reviewerFeedback?: IReviewerFeedback[];
  invitedReviewers?: IInvitedReviewer[];
  declineReason?: string;
};

export type ISubmissionRejection = {
  dateEntered: string;
  rejectionReason?: string;
  editorComment?: string;
  reviewerFeedback?: IReviewerFeedback[];
  attachments?: ISubmissionFile[];
};

export type ISubmissionWithdrawal = {
  requestedByAuthor: boolean;
  withdrawalReason?: string;
  editorComment?: string;
  authorResponse?: string;
  attachments?: ISubmissionFile[];
};

export type IPreProduction = {
  editorComment?: string;
  dateEntered?: string;
};

export type ICloneSource = {
  previousSubmissionId: string;
  previousManuscriptId?: string;
  clonedByUserId: string;
  clonedAt: string;
};

export type IUnsubmit = {
  editorComment: string;
  attachments?: ISubmissionFile[];
};

export type IUnsubmitHistoryItem = {
  version: number;
  unsubmittedAt: string;
  resubmittedAt?: string;
};

export type IRevisionHistory = {
  journalName: string;
  rounds: IRevisionRound[];
};

export type ITransferHistoryTarget = {
  submissionContextId: string;
  articleTypeCategoryId: string;
};

export type ITransferHistoryItem = {
  id: string;
  date: string;
} & (
  | {
      type: 'reject';
      fromSubmissionContextId: string;
      fromArticleTypeCategoryId: string;
    }
  | {
      type: 'accept';
      fromSubmissionContextId: string;
      fromArticleTypeCategoryId: string;
      toSubmissionContextId: string;
      toArticleTypeCategoryId: string;
      fromManuscriptId?: string;
      toManuscriptId?: string;
    }
  | {
      type: 'offer';
      fromSubmissionContextId: string;
      fromArticleTypeCategoryId: string;
      fromDescription?: string;
      fromManuscriptId?: string;
      targets: ITransferHistoryTarget[];
      reviewerFeedback?: {
        recommendation?: string;
        comment: string;
      }[];
      recommendationAssignees?: IParticipant[];
      recommendationRounds?: IRecommendationRound[];
      recommendationWorkflowData?: IRecommendationWorkflowData | null;
    }
);

export type StmDataStatus = 'waiting' | 'complete' | 'error_sending';

export type IStmData = {
  status: StmDataStatus;
  requestURL?: string;
  requestPayload?: string;
  responsePayload?: string;
  requestedAt?: string;
  receivedAt?: string;
  error?: string;
};

export type IRecommendationAttachment = ISubmissionFile & {
  visibility?: string;
  addedBy?: string;
};

export type IRecommendation = {
  id?: string;
  decision?: string;
  commentForAuthor?: string;
  moderatedCommentForAuthor?: string;
  commentForEditor?: string;
  attachments?: IRecommendationAttachment[];
  moderatedAttachments?: IRecommendationAttachment[];
} | null;

export type IRecommendationParticipant = {
  role: string;
  userId: string;
  userFirstName?: string;
  userLastName?: string;
  status: string;
  assignedAt: string;
  completedAt?: string;
  moderatedAt?: string;
  recommendation: IRecommendation;
  submissionWorkflowPhase?: string;
};

export type IRecommendationCycle = {
  participants: IRecommendationParticipant[];
};

export type IRecommendationRound = {
  // createdAt: string; // ISO date string
  dateEntered?: string;
  dateCompleted?: string;
  cycle: IRecommendationCycle[];
  revisionNumber?: number;
};

/** CoC rounds may omit revisionNumber on legacy data; treat as original submission (0). */
export function normalizeRecommendationRevisionNumber(
  revisionNumber: number | undefined | null
): number {
  if (revisionNumber == null || Number.isNaN(Number(revisionNumber))) {
    return 0;
  }
  return Number(revisionNumber);
}

/**
 * Keep prior manuscript-revision CoC rounds and start one for the current revision
 * only when missing (e.g. first approve for peer review). Does not append cycles
 * on an existing revision round — author resubmit already creates the awaiting cycle.
 */
export function ensureCocRecommendationRoundForRevision(
  existingRounds: IRecommendationRound[] | undefined,
  revisionNumber: number,
  participants: IRecommendationParticipant[]
): IRecommendationRound[] {
  const targetRevision = normalizeRecommendationRevisionNumber(revisionNumber);
  const rounds: IRecommendationRound[] = Array.isArray(existingRounds)
    ? existingRounds.map(round => ({
        ...round,
        cycle: (round.cycle ?? []).map(cycle => ({
          ...cycle,
          participants: [...(cycle.participants ?? [])]
        }))
      }))
    : [];

  const hasRoundForRevision = rounds.some(
    round =>
      normalizeRecommendationRevisionNumber(round.revisionNumber) ===
      targetRevision
  );

  if (hasRoundForRevision) {
    return rounds;
  }

  rounds.push({
    revisionNumber: targetRevision,
    dateEntered: new Date().toISOString(),
    cycle: [{ participants }]
  });

  return rounds;
}

export type IRecommendationWorkflowData = {
  currentState?: string;
  currentRole?: string;
  awaitingRole?: string;
  dateEnteredCurrentPhase?: string;
};

type ExportStatus = 'SUCCESS' | 'FAILED';
export type Exports = {
  [vendorName: string]: ExportStatus;
};

export type ISubmissionWithCaseData = {
  id: string;
  submissionContextId: string;
  organizationId: string;
  workspaceId: string;
  portfolioId: string;
  participants: IParticipant[];
  articleWorkflowId: string;
  articleTypeCategoryId: string;
  workflowData: IWorkflowData;
  caseData: {
    submission: ISubmission;
    decision?: string;
    editorialAssignees: IParticipant[];
    feedback: IFeedback[];
    activeRevisionRound?: IRevisionRound;
    currentRevisionHistory: IRevisionHistory;
    previousRevisionHistory?: (IRevisionHistory & {
      id: string;
      dateEntered: string;
    })[];

    tags?: string[];
    transferOffer?: ITransferOffer;
    rejection?: ISubmissionRejection;
    preProduction?: IPreProduction;
    cloneSource?: ICloneSource;
    changeRequest?: IChangeRequestWithSubmissionData;
    copyrightForm?: ICopyrightForm;
    withdrawal?: ISubmissionWithdrawal;
    unsubmit?: IUnsubmit;
    unsubmitHistory?: IUnsubmitHistoryItem[];
    transferHistory?: ITransferHistoryItem[];
    stmData?: IStmData;
    authorCopyrightForm?: IAuthorCopyrightForm[];
    authorICMJEForm?: IAuthorICMJEForm[];
    recommendationAssignees?: IParticipant[];
    recommendationRounds?: IRecommendationRound[];
    recommendationWorkflowData?: IRecommendationWorkflowData;
    integrityCheckStatus?: number;
    exports?: Exports;
    isUADDetected?: boolean;
  };
  syntheticVariables?: ISubmissionSyntheticVariables;
  created_at?: string;
  manuscriptId?: string;
  originalManuscriptId?: string;
  revisionNumber?: number;
};

export const IDashboardViews = [
  'submissions',
  'my-submissions-draft',
  'my-submissions-active',
  'my-submissions-revision',
  'my-submissions-unsubmitted',
  'my-submissions-completed',
  'reviews-active',
  'reviews-completed',
  'early-assessment-completed',
  'early-assessment-active',
  'adjudication-completed',
  'adjudication-active'
];

export const ALLOWED_ROLES_FOR_BRANDING = ['reviewer'];

export type EmailDeliveryState =
  | 'neutral'
  | 'attention'
  | 'caution'
  | 'success'
  | 'interactive';

export const EMAIL_DELIVERY_STATUS_MAP: Record<string, EmailDeliveryState> = {
  processed: 'interactive',

  delivered: 'success',

  deferred: 'attention',

  bounce: 'caution',

  dropped: 'caution',

  open: 'success',

  click: 'success',

  spamreport: 'caution',

  unsubscribe: 'caution',

  group_unsubscribe: 'caution',

  group_resubscribe: 'neutral'
};

type EmailDeliveryStatus =
  | 'Email Verified'
  | 'Delivered'
  | 'Pending'
  | 'Temporary Issue'
  | 'Invalid Email'
  | 'Blocked'
  | 'Engaged'
  | 'Spam Reported'
  | 'Unsubscribed';

export const EMAIL_DELIVERY_NAME_MAP: Record<string, EmailDeliveryStatus> = {
  processed: 'Pending', // accepted by SendGrid, not delivered yet

  delivered: 'Email Verified',

  deferred: 'Temporary Issue',

  bounce: 'Invalid Email',

  dropped: 'Blocked',

  open: 'Engaged',

  click: 'Engaged',

  spamreport: 'Spam Reported',

  unsubscribe: 'Unsubscribed',

  group_unsubscribe: 'Unsubscribed',

  group_resubscribe: 'Email Verified'
};
